import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { AmountInput, Button, DatePicker, Drawer, Icon, SearchSelect, StatusChip } from '../components'
import { useAccounts } from '../lib/api/accounts'
import { useCategories } from '../lib/api/categories'
import { useCounterparties } from '../lib/api/counterparties'
import { useLatestFxRate } from '../lib/api/fx-rates'
import { useOrganizations } from '../lib/api/organizations'
import { usePayVendor } from '../lib/api/licenses'
import { ApiError } from '../lib/api'
import type { LicenseDetail } from '../lib/api/types'
import { fmtMoney } from '../lib/money'

// Default expense category for a license cost (себестоимость) payment. Resolved
// by name so it stays correct across databases; falls back to the first expense
// category if absent.
const LICENSE_COST_CATEGORY_NAME = 'Собівартість ліцензій'

// Records a vendor (cost) payment for a license. The sale side isn't tracked —
// a license only syncs in after the client invoice is fully paid.
export function LicensePaymentDrawer({
  detail,
  onClose,
}: {
  detail: LicenseDetail
  onClose: () => void
}) {
  const tariff = detail.tariff
  if (!tariff) return null
  const paidOut = detail.payments
    .filter((p) => p.direction === 'out')
    .reduce((s, p) => s + p.amount, 0)
  return <PayVendorForm detail={detail} tariff={tariff} paidOut={paidOut} onClose={onClose} />
}

function PayVendorForm({
  detail,
  tariff,
  paidOut,
  onClose,
}: {
  detail: LicenseDetail
  tariff: NonNullable<LicenseDetail['tariff']>
  paidOut: number
  onClose: () => void
}) {
  const license = detail.license
  // The license's own vendor discount (if any) already netted out of the cost.
  const cost = detail.costEffective ?? tariff.costPrice
  const outstanding = Math.max(0, cost - paidOut)

  const accounts = useAccounts()
  const organizations = useOrganizations()
  const counterparties = useCounterparties()
  const expenseCategories = useCategories({ kind: 'expense' })
  const vendor = (counterparties.data ?? []).find(
    (c) => c.id === tariff.vendorCounterpartyId,
  )

  const allAccounts = useMemo(() => accounts.data ?? [], [accounts.data])
  const orgMap = useMemo(
    () => new Map((organizations.data ?? []).map((o) => [o.id, o.name])),
    [organizations.data],
  )
  // Default account: prefer one in the currency we're actually paying (the tariff
  // cost currency, usually EUR) so the payment books natively, with no FX
  // conversion. Within that currency honor the vendor's configured default
  // account; otherwise fall back to the vendor default, then any account.
  const preferredAccountId = useMemo(() => {
    const inCostCurrency = allAccounts.filter((a) => a.currency === tariff.costCurrency)
    const vendorDefaultInCcy = inCostCurrency.find(
      (a) => a.id === vendor?.defaultPaymentAccountId,
    )
    return (
      vendorDefaultInCcy?.id ??
      inCostCurrency[0]?.id ??
      vendor?.defaultPaymentAccountId ??
      allAccounts[0]?.id ??
      ''
    )
  }, [allAccounts, tariff.costCurrency, vendor?.defaultPaymentAccountId])

  const [amount, setAmount] = useState(outstanding.toString())
  const [accountId, setAccountId] = useState<string>('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [categoryId, setCategoryId] = useState<string>('')
  const [description, setDescription] = useState(
    license.counterpartyName
      ? `Оплата вендору ${tariff.vendorName} за клієнта ${license.counterpartyName}`
      : `Оплата вендору: ${tariff.vendorName}`,
  )

  // Seed the account once data is in. Only auto-fills while the user hasn't
  // picked one yet (empty), so it never overrides a manual choice.
  useEffect(() => {
    if (!accountId && preferredAccountId) setAccountId(preferredAccountId)
  }, [accountId, preferredAccountId])

  useEffect(() => {
    if (categoryId || !expenseCategories.data || expenseCategories.data.length === 0) return
    const preferred = expenseCategories.data.find(
      (c) => c.name.trim().toLowerCase() === LICENSE_COST_CATEGORY_NAME.toLowerCase(),
    )
    setCategoryId((preferred ?? expenseCategories.data[0]).id)
  }, [categoryId, expenseCategories.data])

  const account = allAccounts.find((a) => a.id === accountId)
  const needsFx = account != null && account.currency !== tariff.costCurrency
  const fxCurrency: 'USD' | 'EUR' = tariff.costCurrency === 'EUR' ? 'EUR' : 'USD'
  const fxRate = useLatestFxRate(fxCurrency, date)
  const computedBase = needsFx && fxRate.data
    ? Math.round(Number(amount) * fxRate.data.buyRate * 100) / 100
    : null

  const mutation = usePayVendor()
  const [error, setError] = useState<string | null>(null)
  // The payment is saved even when the B24 stage push fails, so we keep the
  // drawer open on a warning instead of closing silently — otherwise the CRM
  // card is left in a debt stage with nobody aware of it.
  const [warning, setWarning] = useState<string | null>(null)

  const submit = async () => {
    setError(null)
    try {
      const res = await mutation.mutateAsync({
        licenseId: license.id,
        amount: Number(amount),
        accountId,
        date,
        categoryId,
        description,
      })
      if (res.warning) {
        setWarning(translatePayWarning(res.warning))
        return
      }
      onClose()
    } catch (err) {
      setError(translatePayError(err))
    }
  }

  return (
    <Drawer
      onClose={onClose}
      title={`Оплата вендору — ${tariff.vendorName}`}
      width={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 16 }}>
        {allAccounts.length === 0 && !accounts.isLoading && (
          <div
            style={{
              padding: 10,
              borderRadius: 6,
              background: 'var(--bg-3)',
              border: '1px solid var(--warning-fg)',
              fontSize: 13,
              color: 'var(--warning-fg)',
            }}
          >
            Немає жодного рахунку. Створіть рахунок у Налаштуваннях → Рахунки.
          </div>
        )}
        {expenseCategories.data?.length === 0 && !expenseCategories.isLoading && (
          <div
            style={{
              padding: 10,
              borderRadius: 6,
              background: 'var(--bg-3)',
              border: '1px solid var(--warning-fg)',
              fontSize: 13,
              color: 'var(--warning-fg)',
            }}
          >
            Немає категорій типу «Витрата». Створіть категорію у Налаштуваннях → Категорії.
          </div>
        )}
        <Field label="Сума">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <AmountInput
              value={amount}
              onChange={setAmount}
              decimals={2}
              className="input num"
              style={{ flex: 1 }}
            />
            <StatusChip status={tariff.costCurrency} />
          </div>
          <Hint>
            Залишок до сплати: {fmtMoney(outstanding, tariff.costCurrency)}
            {tariff.allowOverpay && ' · переплата дозволена'}
          </Hint>
        </Field>

        <Field label="Рахунок">
          <SearchSelect
            className="input"
            value={accountId}
            onChange={setAccountId}
            placeholder="Оберіть рахунок…"
            options={allAccounts.map((a) => ({
              id: a.id,
              label: a.name,
              hint: `${orgMap.get(a.organizationId) ?? '—'} · ${a.currency}`,
            }))}
          />
        </Field>

        <Field label="Дата">
          <DatePicker className="input" value={date} onChange={setDate} />
        </Field>

        <Field label="Категорія">
          <SearchSelect
            className="input"
            value={categoryId}
            onChange={setCategoryId}
            placeholder="Оберіть категорію…"
            options={(expenseCategories.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
          />
        </Field>

        <Field label="Опис">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input"
          />
        </Field>

        {needsFx && fxRate.data && computedBase != null && (
          <div
            style={{
              padding: 10,
              borderRadius: 6,
              background: 'var(--bg-3)',
              fontSize: 12,
              color: 'var(--fg-2)',
            }}
          >
            <Icon name="arrow-left-right" size={12} /> 1 {tariff.costCurrency} ={' '}
            {fxRate.data.buyRate.toFixed(2)} {account?.currency} (джерело: {fxRate.data.source}).
            <br />
            Сума у валюті рахунку: <strong>{fmtMoney(computedBase, account!.currency)}</strong>
          </div>
        )}

        {error && (
          <div style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{error}</div>
        )}

        {warning && (
          <div
            style={{
              padding: 10,
              borderRadius: 6,
              background: 'var(--bg-3)',
              border: '1px solid var(--warning-fg)',
              fontSize: 13,
              color: 'var(--warning-fg)',
            }}
          >
            {warning}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {warning ? (
            <Button variant="primary" size="sm" onClick={onClose}>
              Зрозуміло
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={onClose}>
                Скасувати
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={submit}
                disabled={mutation.isPending || !amount || !accountId || !categoryId}
              >
                {mutation.isPending
                  ? 'Оплачуємо…'
                  : `Сплатити ${fmtMoney(Number(amount) || 0, tariff.costCurrency)}`}
              </Button>
            </>
          )}
        </div>
      </div>
    </Drawer>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 11,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  )
}

function Hint({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{children}</span>
}

// The payment is already recorded when a warning comes back — it only tells the
// operator what still needs doing by hand in B24.
function translatePayWarning(code: string): string {
  const map: Record<string, string> = {
    b24_stage_update_failed:
      'Оплату записано, борг за ліцензією закрито. Але стадію в B24 не оновлено — ' +
      'портал не відповів. Перенесіть картку ліцензії у «Ліцензія куплена» вручну.',
  }
  return map[code] ?? `Оплату записано, але: ${code}`
}

function translatePayError(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.body) as { error?: string }
      const code = parsed.error ?? ''
      const map: Record<string, string> = {
        missing_fx_rate: 'Курс на цю дату не знайдено. Додайте курс у Налаштуваннях → Курси.',
        no_tariff: 'У ліцензії не зіставлено тариф — створіть або виберіть тариф у Налаштуваннях.',
        amount_out_of_range: 'Сума більша за залишок до сплати.',
        account_not_found: 'Рахунок не знайдено.',
        bad_category: 'Категорія має бути типу «витрата».',
        duplicate_link: 'Ця транзакція вже привʼязана у цьому напрямку.',
        transaction_not_found: 'Транзакція не знайдена.',
        wrong_tx_type: 'Тип транзакції не підходить (in→income, out→expense).',
        currency_mismatch: 'Валюта транзакції не збігається з валютою ліцензії/тарифу.',
        license_not_found: 'Ліцензія не існує.',
      }
      return map[code] ?? `Помилка: ${code || err.statusText || 'невідомо'}`
    } catch {
      return `Помилка: ${err.statusText}`
    }
  }
  return err instanceof Error ? err.message : 'Невідома помилка'
}
